import React from "react";

import {
  Box,
  Button,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  FormHelperText,
  makeStyles,
  OutlinedInput,
  TextField,
  Typography,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { useMutation } from "react-query";

import { GENERIC_ERROR } from "client/constants";
import { useContactUs } from "client/context/contact-us-context";
import { useMerchantModel } from "client/hooks";
import { logAmplitudeEvent } from "client/lib/amplitude";
import { UserService } from "client/services";
import { getEmail, isValidEmail } from "client/utils";

import { FileUploadField } from "../FormBuilder/fields/FileUploadField";
import { SuccessIcon } from "../OnboardingStatus/Steps/Instore/SelfServeActivation.components";

const useStyles = makeStyles((theme) => ({
  header: {
    fontWeight: theme.typography.fontWeightBold,
  },
  contactBtn: {
    margin: theme.spacing(1),
    height: theme.spacing(5),
  },
  iconButton: {
    cursor: "pointer",
    transition: "all .2s ease-in-out",
    "&:hover": {
      transform: "scale(1.1)",
    },
  },
  input: {
    width: "100%",
  },
  textArea: {
    // set back to initial height, our theme has a default of 64px
    height: "initial",
  },
  backButton: {
    "&.MuiButton-contained": {
      border: `1px solid ${theme.palette.common.black}`,
      background: theme.palette.common.white,
    },
  },
}));

export const ContactUsButton: React.FC<{
  setContactUsOpen: () => void;
}> = ({ setContactUsOpen }) => {
  const classes = useStyles();
  return (
    <Box display="flex" alignItems={"center"}>
      <Typography variant="body2">Need help?</Typography>
      <Button
        onClick={setContactUsOpen}
        variant="outlined"
        size="medium"
        color="secondary"
        className={classes.contactBtn}
      >
        Contact Us
      </Button>
    </Box>
  );
};

export const ContactUs: React.FC = () => {
  const { merchant, formatMerchantForContactUsInfo } = useMerchantModel();
  const { contactUsOpen, setContactUsClose, showContactUs } = useContactUs();
  const [file, setFile] = React.useState<File | undefined>(undefined);
  const [error, setError] = React.useState("");
  const [requestText, setRequestText] = React.useState("");
  const [success, setSuccess] = React.useState(false);

  const [merchantEmail, setMerchantEmail] = React.useState(getEmail(merchant));
  const classes = useStyles();

  const { mutate: submit } = useMutation(UserService.completeContactUsForm, {
    onError: () => {
      setError(GENERIC_ERROR);
    },
    onSuccess: (response) => {
      if (response.message) {
        logAmplitudeEvent("ONBOARDING_CONTACT_US", "contact_us_submit", {
          source: contactUsOpen ?? "unknown",
        });
        setSuccess(true);
      }
    },
  });

  const getFullName = () => {
    const { firstName, lastName } = merchant.lead;
    return `${firstName.value} ${lastName.value}`;
  };

  const renderSuccess = () => {
    if (file) {
      return "File uploaded successfully!";
    }
    return "";
  };

  const clearError = () => setError("");

  const handleSubmit = (
    e: React.FormEvent<HTMLFormElement> | React.MouseEvent
  ) => {
    e.preventDefault();
    clearError();
    if (!isValidEmail(merchantEmail)) {
      return setError("Please enter a valid email address");
    }

    const data = formatMerchantForContactUsInfo(
      requestText,
      merchantEmail,
      file
    );
    submit(data);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.persist();
    e.preventDefault();
    const file = e.currentTarget?.files?.[0];
    setFile(file);
  };

  const clearModalState = () => {
    setSuccess(false);
    setRequestText("");
    setFile(undefined);
    setError("");
  };

  const hasBeenOpenedRef = React.useRef(false);

  React.useEffect(() => {
    // we do this to prevent the modal from firing the event when it first mounts, only when it opens
    if (contactUsOpen) {
      hasBeenOpenedRef.current = true;
    } else if (hasBeenOpenedRef.current) {
      logAmplitudeEvent("ONBOARDING_CONTACT_US", "contact_us_close", {
        source: contactUsOpen ?? "unknown",
      });
      clearModalState();
    }
  }, [contactUsOpen]);

  const successState = (
    <Box
      display="flex"
      flexDirection={"column"}
      alignItems={"center"}
      justifyContent={"center"}
      minHeight={600}
      gridGap={32}
      textAlign={"center"}
    >
      <Box>
        <SuccessIcon size="large" />
      </Box>
      <Box>
        <Typography variant="body1">
          <strong>
            Thank you for submitting your request! <br />
            Our team will be in touch within 2-3 business days.
          </strong>
        </Typography>
      </Box>
      <Box minWidth={240}>
        <Button
          className={classes.backButton}
          variant="contained"
          onClick={setContactUsClose}
          component="span"
          fullWidth
        >
          <strong>Back</strong>
        </Button>
      </Box>
    </Box>
  );

  if (!showContactUs) {
    return null;
  }

  return (
    <Container fixed={true}>
      <Dialog
        aria-describedby="contact us"
        aria-labelledby="contact-us-modal"
        fullWidth
        maxWidth="md"
        onClose={setContactUsClose}
        open={Boolean(contactUsOpen)}
      >
        <Box>
          <Box display="flex" justifyContent="flex-end" m={3}>
            <CloseIcon
              className={classes.iconButton}
              onClick={setContactUsClose}
            />
          </Box>
          <Box pl={{ lg: 12 }} pr={{ lg: 20 }} mb={9}>
            <DialogTitle id="contact-modal">
              <Box display="flex" justifyContent="center">
                <Typography className={classes.header} variant="h5">
                  Merchant Support
                </Typography>
              </Box>
            </DialogTitle>
            <DialogContent>
              {success ? (
                successState
              ) : (
                <form onSubmit={handleSubmit}>
                  <Box
                    maxWidth={{ xs: "100%", sm: "80%" }}
                    display={"grid"}
                    gridGap={8}
                    margin={"auto"}
                  >
                    <TextField
                      className={classes.input}
                      disabled
                      type="text"
                      value={getFullName()}
                      variant="outlined"
                    />
                    <TextField
                      className={classes.input}
                      type="text"
                      value={merchantEmail}
                      label="Email"
                      onChange={(e) => setMerchantEmail(e.currentTarget.value)}
                      variant="outlined"
                    />
                    <Box mt={2}>
                      <Typography className={classes.header} variant="body2">
                        Tell us about your request.
                      </Typography>
                      <OutlinedInput
                        onChange={(e) => {
                          e.preventDefault();
                          setRequestText(e.currentTarget.value);
                        }}
                        value={requestText}
                        classes={{ root: classes.textArea }}
                        multiline
                        rows={8}
                        fullWidth
                      />
                    </Box>
                    <Box mt={2}>
                      <Typography className={classes.header} variant="body2">
                        If applicable, please provide a screenshot.
                      </Typography>

                      <Box width={"100%"}>
                        <FileUploadField
                          hasError={false}
                          id="contact-us-upload"
                          propertyName="agreement-upload"
                          updateFieldValue={(_) => {
                            // do nothing
                          }}
                          handleFileChange={handleFileChange}
                          helperText={renderSuccess()}
                        />
                      </Box>
                    </Box>
                    <Box margin="auto">
                      {error && (
                        <FormHelperText error={true}>
                          <strong>{error}</strong>
                        </FormHelperText>
                      )}
                    </Box>
                    <Box display={"flex"} gridGap={8}>
                      <Button
                        className={classes.backButton}
                        fullWidth
                        variant="contained"
                        onClick={setContactUsClose}
                        component="span"
                      >
                        <strong>Back</strong>
                      </Button>
                      <Button
                        color="primary"
                        fullWidth
                        disabled={!requestText}
                        variant="contained"
                        type="submit"
                      >
                        <strong>Submit</strong>
                      </Button>
                    </Box>
                  </Box>
                </form>
              )}
            </DialogContent>
          </Box>
        </Box>
      </Dialog>
    </Container>
  );
};
